import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  FormControl,
  FormLabel,
  Textarea,
  Box,
  Alert,
  AlertDescription,
  AlertIcon,
} from '@chakra-ui/react';
import { useRef, useMemo, useState, useEffect } from 'react';
import { stagesList } from '@/constants/contact-stages';
import { SanitizedText } from '@/ui/SanitizedDom';
import { useGetContactRejectionReasonQuery } from '@/store/query/contacts';
import { Formik, Form, Field } from 'formik';
import { FormSelect } from '@/components/form/FormSelect';
import { FormSelect as DesignFormSelect } from '@/design-system/select';

import { rejectionStage } from '@/constants/contact-stages';
import {
  useMoveStageMutation,
  useRejectStageMutation,
} from '@/store/query/contacts';
import * as Yup from 'yup';
import { useMixpanel } from '@/hooks/use-mixpanel';
import {
  useMoveFlowStageMutation,
  useRejectFlowStageMutation,
} from '@/store/query/outreachCandidates';
import { useGetATSRejectionListQuery } from '@/store/query/ats';
import { KText } from '@/ui-library/components/text';
import { useShowToast } from '@/hooks/useShowToast';
import { useKFlags } from '@/hooks/use-k-flags';
import { REJECT_REASON_GROUPS } from '@/constants/reject-reasons';
import { XCircle } from '@kula-ai/icons-core';

const GroupHeading = (props) => (
  <KText size="sm-semi-bold" mb="6px" ml="4px">
    {props.children}
  </KText>
);

const groupOptions = (options) => {
  const groups = {};

  // Group options based on the "group" property
  options?.forEach((option) => {
    const groupName = option.group;
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    // Only add enabled and not deleted items
    if (option?.enabled && !option?.deleted && !option?.system_default)
      groups[groupName].push(option);
  });

  // Convert the grouped options into the desired format and filter out empty groups
  const groupedOptions = Object.entries(groups)
    .filter(([_, groupOptions]) => groupOptions.length > 0) // Filter out empty groups
    .map(([label, groupOptions]) => ({
      label: REJECT_REASON_GROUPS?.find((rejRsn) => rejRsn?.id === label)
        ?.label,
      options: groupOptions,
    }));

  return groupedOptions;
};

const stageRejectionSchema = Yup.object().shape({
  reasonId: Yup.object().required('Rejection reason is required'),
});

const getATSConfirmationText = ({ candidate, targetStage, jobName }) => {
  return `Are you sure you want to move <b>${
    candidate?.full_name || candidate?.first_name
  }</b> to the <b>${targetStage?.label}</b> stage in <b>${jobName}</b> job?`;
};

const getConfirmationText = ({ fromStage, candidate, targetStage }) => {
  if (stagesList[fromStage]?.label) {
    return `Are you sure to move <b>${
      candidate?.full_name || candidate?.first_name
    }</b> from <b>${stagesList[fromStage]?.label}</b> to <b>${
      targetStage?.label
    }</b>?`;
  }
  return `Are you sure to move <b>${
    candidate?.full_name || candidate?.first_name
  }</b>  to <b>${targetStage?.label}</b>?`;
};

const RejectStage = ({
  candidate,
  fromStage,
  targetStage,
  onSaveSuccess = () => {},
  onClose = () => {},
  outreach = {},
}) => {
  const { track } = useMixpanel();
  const toast = useShowToast();
  const { tContactStagePhase2 } = useKFlags();
  const [error, setError] = useState(null);
  const [rejectStage, { isLoading }] = useRejectStageMutation();
  const [rejectFlowStage, { isLoading: isFlowStageLoading }] =
    useRejectFlowStageMutation();
  const { data, isLoading: isRejectionReasonLoading } =
    useGetContactRejectionReasonQuery({
      contactId: candidate?.id,
    });

  const onRejectStage = async (values) => {
    try {
      if (tContactStagePhase2) {
        const result = await rejectFlowStage({
          outreachId: outreach?.id,
          candidateId: candidate?.id,
          stage: targetStage?.id,
          ...values,
        });
        const updateStageError = result?.data?.[0]?.errors;
        // ATS Error
        if (updateStageError?.contact?.base?.length) {
          setError(updateStageError?.contact?.base[0]);
          return;
        }
        if (updateStageError?.length || updateStageError?.contact) {
          toast({
            title: 'ATS stage update failed',
            description: updateStageError[0],
            position: 'bottom-left',
            status: 'error',
            isClosable: true,
          });
          onClose();
          return;
        }
        toast({
          title: 'ATS stage updated',
          position: 'bottom-left',
          status: 'success',
          isClosable: true,
        });
        onClose();
        onSaveSuccess();
      } else {
        const result = await rejectStage({
          ids: [candidate?.id],
          stage: targetStage?.id,
          ...values,
        });
        const updateStageError = result?.data?.[0]?.errors;
        // ATS Error
        if (updateStageError?.contact?.base?.length) {
          setError(updateStageError?.contact?.base[0]);
          return;
        }
        if (updateStageError?.length || updateStageError?.contact) {
          toast({
            title: 'Contact stage update failed',
            description: updateStageError[0],
            position: 'bottom-left',
            status: 'error',
            isClosable: true,
          });
          onClose();
          return;
        }
        toast({
          title: 'Contact stage updated',
          position: 'bottom-left',
          status: 'success',
          isClosable: true,
        });
        onClose();
        track({
          event: 'outreach-stage-changed',
          prop: {
            target_stage: targetStage?.id,
          },
        });
        onSaveSuccess();
      }
    } catch {
      // best-effort; ignore failures
    }
  };

  const validationSchema = useMemo(() => {
    let schema = stageRejectionSchema;
    if (!data?.rejection_reasons?.length) {
      schema = schema.omit(['reasonId']);
    }
    return schema;
  }, [data]);

  return (
    <>
      <ModalHeader>
        <Text size="lg-semi-bold">Update Stage</Text>
        <SanitizedText
          size="sm"
          fontWeight="400"
          variant="muted"
          html={getConfirmationText({ candidate, fromStage, targetStage })}
        />
      </ModalHeader>
      <ModalBody>
        {error && (
          <Alert
            mb="12px"
            status="error"
            borderRadius="6px"
            bg="var(--kula-dark-secondary-25, #FFF1F1)"
            border="1px solid #DA1E28"
          >
            <AlertIcon as={XCircle} w="24px" h="24px" />
            <AlertDescription fontWeight="400" fontSize="14px">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {!isRejectionReasonLoading && (
          <Formik
            validateOnBlur={false}
            initialValues={{
              reasonId: '',
              note: '',
            }}
            validationSchema={validationSchema}
            onSubmit={onRejectStage}
          >
            <Form noValidate>
              {data?.rejection_reasons?.length > 0 && (
                <>
                  <Alert
                    status="info"
                    borderRadius="8px"
                    mb="16px"
                    bg="var(--kula-dark-primary-50, #DEEBFF)"
                  >
                    <AlertIcon
                      alignSelf="normal"
                      color="var(--kula-dark-primary-700, #0747A6)"
                    />
                    <Text>
                      The rejection reason will be synced with your ATS and will
                      be accessible from there as well.
                    </Text>
                  </Alert>
                  <Field name="reasonId">
                    {({ form }) => (
                      <FormControl mb="12px" isRequired>
                        <FormLabel
                          fontWeight="semiBold"
                          fontSize="14px"
                          htmlFor="reasonId"
                        >
                          Rejection reasons
                        </FormLabel>

                        <FormSelect
                          placeholder="Select"
                          options={groupOptions(data?.rejection_reasons)}
                          fieldName="reasonId"
                          _hover={{
                            border: '1px solid',
                            borderColor: 'border-color',
                          }}
                          _focus={{
                            border: '1px solid',
                            borderColor: 'border-color',
                          }}
                          components={{
                            GroupHeading,
                          }}
                        />

                        {form.errors.reasonId && (
                          <Box
                            color="#DA1E28"
                            fontSize="14px"
                            fontWeight="400px"
                            mt="6px"
                          >
                            {form.errors.reasonId}
                          </Box>
                        )}
                      </FormControl>
                    )}
                  </Field>
                </>
              )}

              <Field name="note">
                {({ field, form }) => (
                  <FormControl mb="12px">
                    <FormLabel
                      fontWeight="semiBold"
                      fontSize="14px"
                      htmlFor="note"
                    >
                      Add comments
                    </FormLabel>
                    <Textarea
                      {...field}
                      id="note"
                      name="note"
                      isInvalid={form.touched.note && form.errors.note}
                      placeholder="Write your comments here."
                    />
                    {form.touched.note && form.errors.note && (
                      <Box
                        color="#DA1E28"
                        fontSize="14px"
                        fontWeight="400px"
                        mt="6px"
                      >
                        {form.errors.note}
                      </Box>
                    )}
                  </FormControl>
                )}
              </Field>
              <ModalFooter pr="0">
                <Button variant="secondary" mr="8px" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  _hover={{ bg: '#EB4A27' }}
                  bg="#EB4A27"
                  isLoading={
                    tContactStagePhase2 ? isFlowStageLoading : isLoading
                  }
                  type="submit"
                >
                  Update
                </Button>
              </ModalFooter>
            </Form>
          </Formik>
        )}
      </ModalBody>
    </>
  );
};

const UpdateStage = ({
  candidate,
  fromStage,
  targetStage,
  onSaveSuccess,
  onClose,
  outreach = {},
}) => {
  const { track } = useMixpanel();
  const toast = useShowToast();
  const { tContactStagePhase2 } = useKFlags();
  const [moveStage, { isLoading }] = useMoveStageMutation();
  const [moveFlowStage, { isLoading: isFlowStageLoading }] =
    useMoveFlowStageMutation();
  const onUpdateStage = async () => {
    try {
      if (tContactStagePhase2) {
        const result = await moveFlowStage({
          candidateId: candidate?.id,
          outreachId: outreach?.id,
          stage: targetStage?.id,
        });

        const updateStageError = result?.data?.data?.[0]?.errors;
        if (updateStageError?.length) {
          toast({
            title: 'ATS stage update failed',
            description: updateStageError[0],
            position: 'bottom-left',
            status: 'error',
            isClosable: true,
          });
          onClose();
          return;
        }
        toast({
          title: 'ATS stage updated',
          position: 'bottom-left',
          status: 'success',
          isClosable: true,
        });
        onClose();
        onSaveSuccess();
      } else {
        const result = await moveStage({
          ids: [candidate?.id],
          stage: targetStage?.id,
        });
        const updateStageError = result?.data?.data?.[0]?.errors;
        if (updateStageError?.length) {
          toast({
            title: 'Contact stage update failed',
            description: updateStageError[0],
            position: 'bottom-left',
            status: 'error',
            isClosable: true,
          });
          onClose();
          return;
        }
        toast({
          title: 'Contact stage updated',
          position: 'bottom-left',
          status: 'success',
          isClosable: true,
        });
        onClose();
        track({
          event: 'outreach-stage-changed',
          prop: {
            target_stage: targetStage?.id,
          },
        });
        onSaveSuccess();
      }
    } catch {
      // best-effort; ignore failures
    }
  };

  return (
    <>
      <ModalHeader>
        <Text size="lg-semi-bold">Update Stage</Text>
      </ModalHeader>
      <ModalBody>
        <SanitizedText
          size="sm"
          variant="muted"
          html={getConfirmationText({ candidate, fromStage, targetStage })}
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" mr="8px" onClick={onClose}>
          Cancel
        </Button>
        <Button
          isLoading={tContactStagePhase2 ? isFlowStageLoading : isLoading}
          mr={3}
          onClick={onUpdateStage}
        >
          Confirm
        </Button>
      </ModalFooter>
    </>
  );
};

export const StageConfirmation = ({
  isOpen,
  onClose = () => {},
  onSaveSuccess = () => {},
  candidate,
  fromStage,
  targetStage,
  outreach = {},
}) => {
  const isRejection = rejectionStage?.includes(targetStage?.id);
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        {isRejection ? (
          <RejectStage
            candidate={candidate}
            fromStage={fromStage}
            targetStage={targetStage}
            onClose={onClose}
            onSaveSuccess={onSaveSuccess}
            outreach={outreach}
          />
        ) : (
          <UpdateStage
            candidate={candidate}
            fromStage={fromStage}
            targetStage={targetStage}
            onClose={onClose}
            onSaveSuccess={onSaveSuccess}
            outreach={outreach}
          />
        )}
      </ModalContent>
    </Modal>
  );
};

export const ConfrimIntrested = ({
  isOpen = false,
  candidate = {},
  fromStage = '',
  targetStage = '',
  onSaveSuccess = () => {},
  onClose = () => {},
  outreach = {},
}) => {
  const toast = useShowToast();
  const [moveStage, { isLoading }] = useMoveFlowStageMutation();
  const { ats_enabled } = useKFlags();
  const onUpdateStage = async () => {
    try {
      const result = await moveStage({
        candidateId: candidate?.id,
        stage: targetStage?.id,
        outreachId: outreach?.id,
      });
      const updateStageError = result?.data?.data?.[0]?.errors;
      if (updateStageError?.length) {
        toast({
          title: 'Flow stage update failed',
          description: updateStageError[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        onClose();
        return;
      }
      toast({
        title: 'Flow stage updated',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
      onClose();
      onSaveSuccess();
    } catch {
      // best-effort; ignore failures
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton color="secondary-700" />
          <ModalHeader>
            <KText size="lg-semi-bold" color="secondary-900">
              Update Stage
            </KText>
          </ModalHeader>
          <ModalBody>
            <SanitizedText
              size="sm"
              variant="muted"
              html={
                ats_enabled && outreach?.ats_job?.name
                  ? getATSConfirmationText({
                      candidate,
                      targetStage: {
                        label: outreach?.interested_ats_job_stage?.name,
                      },
                      jobName: outreach?.ats_job?.name,
                    })
                  : getConfirmationText({
                      candidate,
                      fromStage,
                      targetStage,
                    })
              }
              color="secondary-700"
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" mr="8px" onClick={onClose}>
              Cancel
            </Button>
            <Button isLoading={isLoading} mr={3} onClick={onUpdateStage}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const ConfrimNotIntrested = ({
  isOpen = false,
  candidate = {},
  fromStage = '',
  targetStage = '',
  onSaveSuccess = () => {},
  onClose = () => {},
  outreach = {},
  rejectionListObj = {},
}) => {
  const toast = useShowToast();
  const { ats_enabled } = useKFlags();
  const [error, setError] = useState(null);
  const [rejectStage, { isLoading }] = useRejectFlowStageMutation();
  const { data, isLoading: isRejectionReasonLoading } =
    useGetContactRejectionReasonQuery(
      {
        contactId: candidate?.id,
      },
      { skip: ats_enabled }
    );

  const { data: rejectionApiList, isFetching: isRejectionApiLoading } =
    useGetATSRejectionListQuery(
      {},
      { skip: rejectionListObj?.data?.length || !ats_enabled }
    );

  const rejectionList = rejectionApiList || rejectionListObj?.data;
  const isRejectionLoading =
    isRejectionApiLoading || rejectionListObj?.isLoading;

  const formRef = useRef(null);
  const onRejectStage = async (values) => {
    try {
      const result = await rejectStage({
        candidateId: candidate?.id,
        stage: targetStage?.id,
        outreachId: outreach?.id,
        ...values,
        ats_enabled,
      });
      const updateStageError = result?.data?.[0]?.errors;
      // ATS Error
      if (updateStageError?.contact?.base?.length) {
        setError(updateStageError?.contact?.base[0]);
        return;
      }
      if (updateStageError?.length || updateStageError?.contact) {
        toast({
          title: 'Flow stage update failed',
          description: updateStageError[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        onClose();
        return;
      }
      toast({
        title: 'Flow stage updated',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
      onClose();
      onSaveSuccess();
    } catch {
      // best-effort; ignore failures
    }
  };

  const validationSchema = useMemo(() => {
    let schema = stageRejectionSchema;
    if (!data?.rejection_reasons?.length) {
      schema = schema.omit(['reasonId']);
    }
    return schema;
  }, [data]);

  useEffect(() => {
    if (rejectionList?.length > 0) {
      formRef?.current?.setFieldValue(
        'reasonId',
        outreach?.not_interested_ats_rejection_reason
      );
    }
  }, [outreach?.not_interested_ats_rejection_reason, rejectionList]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton color="secondary-700" />
          <ModalHeader>
            <KText size="lg-semi-bold" color="secondary-900">
              {ats_enabled ? 'Reason for rejection?' : 'Update Stage'}
            </KText>
            {ats_enabled ? (
              <KText>Please select a reason for rejecting this prospect</KText>
            ) : (
              <SanitizedText
                size="sm"
                fontWeight="400"
                variant="muted"
                html={getConfirmationText({
                  candidate,
                  fromStage,
                  targetStage,
                })}
                color="secondary-700"
              />
            )}
          </ModalHeader>
          <ModalBody>
            {error && (
              <Alert
                mb="12px"
                status="error"
                borderRadius="6px"
                bg="var(--kula-dark-secondary-25, #FFF1F1)"
                border="1px solid #DA1E28"
              >
                <AlertIcon as={XCircle} w="24px" h="24px" />
                <AlertDescription fontWeight="400" fontSize="14px">
                  {error}
                </AlertDescription>
              </Alert>
            )}
            {(!isRejectionReasonLoading || !isRejectionLoading) && (
              <Formik
                validateOnBlur={false}
                initialValues={{
                  reasonId: outreach?.not_interested_ats_rejection_reason || '',
                  note: '',
                }}
                validationSchema={validationSchema}
                onSubmit={onRejectStage}
                innerRef={formRef}
              >
                <Form noValidate>
                  {(data?.rejection_reasons?.length > 0 ||
                    rejectionList?.length > 0) && (
                    <>
                      {!ats_enabled && (
                        <Alert
                          status="info"
                          borderRadius="8px"
                          mb="16px"
                          bg="var(--kula-dark-primary-50, #DEEBFF)"
                        >
                          <AlertIcon
                            alignSelf="normal"
                            color="var(--kula-dark-primary-700, #0747A6)"
                          />
                          <Text>
                            The rejection reason will be synced with your ATS
                            and will be accessible from there as well.
                          </Text>
                        </Alert>
                      )}
                      <Field name="reasonId">
                        {({ form }) => (
                          <FormControl mb="12px" isRequired>
                            <FormLabel
                              fontWeight="semiBold"
                              fontSize="sm"
                              htmlFor="reasonId"
                              color="secondary-900"
                            >
                              Rejection reasons
                            </FormLabel>

                            {rejectionList?.length > 0 ? (
                              <DesignFormSelect
                                id="reasonId"
                                options={groupOptions(rejectionList)}
                                placeholder="Select a reason"
                                isLoading={isRejectionLoading}
                                getOptionLabel={(rejection) => rejection.reason}
                                getOptionValue={(rejection) => rejection.id}
                                components={{
                                  GroupHeading,
                                }}
                              />
                            ) : (
                              <FormSelect
                                placeholder="Select"
                                options={data?.rejection_reasons}
                                fieldName="reasonId"
                              />
                            )}

                            {form.errors.reasonId && (
                              <Box
                                color="#DA1E28"
                                fontSize="14px"
                                fontWeight="400px"
                                mt="6px"
                              >
                                {form.errors.reasonId}
                              </Box>
                            )}
                          </FormControl>
                        )}
                      </Field>
                    </>
                  )}

                  <Field name="note">
                    {({ field, form }) => (
                      <FormControl mb="12px">
                        <FormLabel
                          fontWeight="semiBold"
                          fontSize="sm"
                          htmlFor="note"
                          color="secondary-900"
                        >
                          Add comments
                        </FormLabel>
                        <Textarea
                          {...field}
                          id="note"
                          name="note"
                          isInvalid={form.touched.note && form.errors.note}
                          placeholder="Write your comments here."
                          background="secondary-25"
                          color="secondary-700"
                          border="1px solid"
                          borderColor="border-color"
                          _hover={{
                            border: '1px solid',
                            borderColor: 'border-color',
                          }}
                          _focus={{
                            border: '1px solid',
                            borderColor: 'border-color',
                          }}
                        />
                        {form.touched.note && form.errors.note && (
                          <Box
                            color="#DA1E28"
                            fontSize="14px"
                            fontWeight="400px"
                            mt="6px"
                          >
                            {form.errors.note}
                          </Box>
                        )}
                      </FormControl>
                    )}
                  </Field>
                  <ModalFooter pr="0">
                    <Button variant="secondary" mr="8px" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      _hover={{ bg: '#EB4A27' }}
                      bg="#EB4A27"
                      isLoading={isLoading}
                      type="submit"
                    >
                      Update
                    </Button>
                  </ModalFooter>
                </Form>
              </Formik>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
